import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  Database, 
  Download, 
  Mail, 
  FileSpreadsheet, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Loader2,
  Trash2,
  RefreshCw,
  HardDrive,
  Users,
  UserCheck,
  DollarSign,
  CalendarCheck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BackupPage() {
  const [loading, setLoading] = useState(false);
  const [backupHistory, setBackupHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  
  // Backup options
  const [email, setEmail] = useState('');
  const [includeStudents, setIncludeStudents] = useState(true);
  const [includeStaff, setIncludeStaff] = useState(true);
  const [includeFees, setIncludeFees] = useState(true);
  const [includeAttendance, setIncludeAttendance] = useState(true);
  const [includeDatabase, setIncludeDatabase] = useState(true);

  const token = localStorage.getItem('token');

  useEffect(() => {
    // Get user email from localStorage
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const user = JSON.parse(userStr);
      setEmail(user.email || '');
    }
    fetchBackupHistory();
  }, []);

  const fetchBackupHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await fetch(`${API_URL}/api/backup/history`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setBackupHistory(data);
      }
    } catch (error) {
      console.error('Failed to fetch backup history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    if (!email) {
      toast.error('Please enter email address');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/backup/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email,
          include_students: includeStudents,
          include_staff: includeStaff,
          include_fees: includeFees,
          include_attendance: includeAttendance,
          include_database: includeDatabase
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success('Backup started! You will receive an email when complete.');
        fetchBackupHistory();
      } else {
        toast.error(data.detail || 'Failed to create backup');
      }
    } catch (error) {
      toast.error('Failed to create backup');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadFile = async (backupId, fileName) => {
    try {
      const response = await fetch(`${API_URL}/api/backup/download/${backupId}/${fileName}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success(`Downloaded ${fileName}`);
      } else {
        toast.error('Failed to download file');
      }
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!window.confirm('Are you sure you want to delete this backup?')) return;

    try {
      const response = await fetch(`${API_URL}/api/backup/${backupId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        toast.success('Backup deleted');
        fetchBackupHistory();
      } else {
        toast.error('Failed to delete backup');
      }
    } catch (error) {
      toast.error('Failed to delete backup');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Completed</Badge>;
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-800"><Loader2 className="w-3 h-3 mr-1 animate-spin" />Processing</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      default:
        return <Badge className="bg-yellow-100 text-yellow-800"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="p-6 space-y-6" data-testid="backup-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Daily Backup</h1>
          <p className="text-gray-600">Create and manage data backups</p>
        </div>
        <Button onClick={fetchBackupHistory} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Create Backup Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            Create New Backup
          </CardTitle>
          <CardDescription>
            Select what data to include in your backup. The backup will be sent to your email.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Email Input */}
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Send backup notification to
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@college.edu"
              data-testid="backup-email-input"
            />
          </div>

          {/* Backup Options */}
          <div className="space-y-4">
            <Label>Include in backup:</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  id="students"
                  checked={includeStudents}
                  onCheckedChange={setIncludeStudents}
                  data-testid="include-students-checkbox"
                />
                <Label htmlFor="students" className="flex items-center gap-2 cursor-pointer">
                  <Users className="w-4 h-4 text-blue-600" />
                  Students
                </Label>
              </div>

              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  id="staff"
                  checked={includeStaff}
                  onCheckedChange={setIncludeStaff}
                  data-testid="include-staff-checkbox"
                />
                <Label htmlFor="staff" className="flex items-center gap-2 cursor-pointer">
                  <UserCheck className="w-4 h-4 text-green-600" />
                  Staff
                </Label>
              </div>

              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  id="fees"
                  checked={includeFees}
                  onCheckedChange={setIncludeFees}
                  data-testid="include-fees-checkbox"
                />
                <Label htmlFor="fees" className="flex items-center gap-2 cursor-pointer">
                  <DollarSign className="w-4 h-4 text-yellow-600" />
                  Fee Payments
                </Label>
              </div>

              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                <Checkbox
                  id="attendance"
                  checked={includeAttendance}
                  onCheckedChange={setIncludeAttendance}
                  data-testid="include-attendance-checkbox"
                />
                <Label htmlFor="attendance" className="flex items-center gap-2 cursor-pointer">
                  <CalendarCheck className="w-4 h-4 text-purple-600" />
                  Attendance
                </Label>
              </div>

              <div className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50 col-span-2 md:col-span-1">
                <Checkbox
                  id="database"
                  checked={includeDatabase}
                  onCheckedChange={setIncludeDatabase}
                  data-testid="include-database-checkbox"
                />
                <Label htmlFor="database" className="flex items-center gap-2 cursor-pointer">
                  <HardDrive className="w-4 h-4 text-red-600" />
                  Full Database
                </Label>
              </div>
            </div>
          </div>

          {/* Create Button */}
          <Button
            onClick={handleCreateBackup}
            disabled={loading}
            className="w-full md:w-auto"
            data-testid="create-backup-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Database className="w-4 h-4 mr-2" />
            )}
            {loading ? 'Creating Backup...' : 'Create Backup'}
          </Button>
        </CardContent>
      </Card>

      {/* Backup History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-gray-600" />
            Backup History
          </CardTitle>
          <CardDescription>
            View and download previous backups
          </CardDescription>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : backupHistory.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Database className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No backups yet</p>
              <p className="text-sm">Create your first backup above</p>
            </div>
          ) : (
            <div className="space-y-4">
              {backupHistory.map((backup) => (
                <div
                  key={backup.id}
                  className="border rounded-lg p-4 hover:bg-gray-50"
                  data-testid={`backup-item-${backup.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        {getStatusBadge(backup.status)}
                        <span className="text-sm text-gray-500">
                          {formatDate(backup.created_at)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600">
                        Sent to: {backup.send_to_email}
                      </p>
                      {backup.files && backup.files.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                          {backup.files.map((file) => (
                            <Button
                              key={file}
                              variant="outline"
                              size="sm"
                              onClick={() => handleDownloadFile(backup.id, file)}
                              data-testid={`download-${file}`}
                            >
                              <Download className="w-3 h-3 mr-1" />
                              {file}
                            </Button>
                          ))}
                        </div>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => handleDeleteBackup(backup.id)}
                      data-testid={`delete-backup-${backup.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <FileSpreadsheet className="w-5 h-5 text-blue-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-900">Backup Information</h3>
              <ul className="text-sm text-blue-700 mt-2 space-y-1">
                <li>• Excel files (.xlsx) will be created for Students, Staff, Fees, and Attendance</li>
                <li>• Full database backup is saved as JSON format</li>
                <li>• You will receive an email notification when backup is complete</li>
                <li>• Download files from the history section above</li>
                <li>• Backups are stored securely for 30 days</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
